import { useContext, useLayoutEffect, useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogWrapper,
} from "../ConsentManagerBanner.styled";
import { Box } from "@otrium/core";
import { Trans } from "@lingui/macro";
import { HyperLink } from "src/atoms/HyperLink";
import { Button } from "src/atoms/Button";
import { CloseButton } from "src/atoms/CloseButton";
import { useLockBodyScroll } from "src/hooks/useLockBodyScroll";
import {
  CategoryPreferences,
  Destination,
} from "@ht-sdks/consent-manager/types/types";
import { theme } from "src/theme";
import { AppCtx } from "src/contexts/app.context";
import { getI18nRoute } from "src/utils/i18nRoutes";
import ConsentSettings from "../ConsentSettings/ConsentSettings";

const ConsentManagerDialog = ({
  destinations,
  preferences,
  setPreferences,
  saveConsent,
  resetPreferences,
}: {
  destinations: Destination[];
  preferences: CategoryPreferences;
  setPreferences: (newPreferences: CategoryPreferences) => void;
  saveConsent: (
    newPreferences?: boolean | CategoryPreferences | undefined,
    shouldReload?: boolean | undefined
  ) => void;
  resetPreferences: () => void;
}): JSX.Element => {
  const [showSettings, setShowSettings] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const {
    displayConsentManager,
    setDisplayConsentManager,
    locale,
    setInitializedConsent,
  } = useContext(AppCtx);

  useLayoutEffect(() => {
    const currentRef = showSettings ? settingsRef : bannerRef;

    if (expanded) {
      setHeight(null);
    } else if (currentRef.current) {
      setHeight(currentRef.current.getBoundingClientRect().height);
    }
  }, [showSettings, preferences, expanded]);

  useLockBodyScroll(displayConsentManager);

  return (
    <DialogWrapper
      data-testid="consent-manager-banner"
      variant={showSettings ? "settings" : "banner"}
    >
      <Dialog height={height !== null ? `${height}px` : "auto"}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={showSettings ? "settings" : "banner"}
            timeout={300}
            classNames="fade"
            onEnter={() => {
              const currentRef = showSettings ? settingsRef : bannerRef;
              if (currentRef.current) {
                setHeight(currentRef.current.getBoundingClientRect().height);
              }
            }}
            unmountOnExit
          >
            <Box padding={4} ref={showSettings ? settingsRef : bannerRef}>
              <DialogHeader
                as="header"
                variant={showSettings ? "settings" : "banner"}
              >
                <DialogTitle as="h4" variant="h4">
                  {showSettings ? (
                    <Trans>consent_manager.settings_title</Trans>
                  ) : (
                    <Trans>consent_manager.banner_title</Trans>
                  )}
                </DialogTitle>
                {showSettings && (
                  <CloseButton
                    onClick={() => {
                      setShowSettings(false);
                      resetPreferences();
                      setExpanded(false);
                    }}
                  />
                )}
                {showSettings && (
                  <DialogDescription data-testid="consent-manager-settings-description">
                    <p>
                      <Trans>consent_manager.settings_description</Trans>{" "}
                    </p>
                    <HyperLink
                      asLink
                      data-testid="show-privacy-statement"
                      href={getI18nRoute({
                        route: "/privacy-statement",
                        locale,
                      })}
                      hasUnderline
                      variant="body.source.semibold14"
                    >
                      <Trans>
                        consent_manager.privacy_and_cookie_statement
                      </Trans>
                    </HyperLink>
                  </DialogDescription>
                )}
              </DialogHeader>
              <DialogBody
                data-testid="consent-manager-body"
                variant={showSettings ? "settings" : "banner"}
              >
                {!showSettings && (
                  <>
                    <span>
                      <Trans>consent_manager.body</Trans>{" "}
                    </span>
                    <HyperLink
                      hasUnderline
                      variant="body.source.semibold14"
                      color={theme.colors.tone.anthraciteDark}
                      onClick={() => setShowSettings(true)}
                    >
                      <Trans>consent_manager.manage_your_preferences</Trans>
                    </HyperLink>
                  </>
                )}
                {showSettings && (
                  <ConsentSettings
                    destinations={destinations}
                    preferences={preferences}
                    setPreferences={setPreferences}
                    setListExpanded={setExpanded}
                  />
                )}
              </DialogBody>
              <DialogFooter variant={showSettings ? "settings" : "banner"}>
                {!showSettings && (
                  <>
                    <Button
                      data-testid="consent-manager-reject-btn"
                      variant="secondary"
                      fullWidth
                      onClick={() => {
                        saveConsent({
                          functional: true,
                          analytics: true,
                          marketing: false,
                        });
                        setInitializedConsent(false);
                        setDisplayConsentManager(false);
                      }}
                    >
                      <Trans>consent_manager.reject_button</Trans>
                    </Button>
                    <Button
                      data-testid="consent-manager-accept-btn"
                      variant="primary"
                      fullWidth
                      onClick={() => {
                        saveConsent({
                          functional: true,
                          analytics: true,
                          marketing: true,
                        });
                        setInitializedConsent(true);
                        setDisplayConsentManager(false);
                      }}
                    >
                      <Trans>consent_manager.accept_all</Trans>
                    </Button>
                  </>
                )}
                {showSettings && (
                  <Button
                    data-testid="consent-manager-save-btn"
                    fullWidth
                    variant="primary"
                    onClick={() => {
                      saveConsent(preferences);
                      if (
                        preferences.analytics ||
                        preferences.marketing ||
                        preferences.functional
                      ) {
                        setInitializedConsent(true);
                      }
                      setDisplayConsentManager(false);
                    }}
                  >
                    <Trans>consent_manager.save_settings_button</Trans>
                  </Button>
                )}
              </DialogFooter>
            </Box>
          </CSSTransition>
        </SwitchTransition>
      </Dialog>
    </DialogWrapper>
  );
};

export default ConsentManagerDialog;
